import './MetricCard.css';

export type MetricStatus = 'excellent' | 'stable' | 'warning' | 'critical';

export type MetricTrendDirection = 'up' | 'down' | 'stable';

/**
 * Change from the previous to the latest measurement (release 1.5.0, 3b).
 * Never colour-only: the arrow comes with visible text ("vorher 6,7") and a
 * screen-reader sentence ("gestiegen von 6,7 auf 7,0 dKH"). Neutral colour —
 * a rise is not "good" or "bad" by itself.
 */
export interface MetricTrend {
  direction: MetricTrendDirection;
  /** Visible short text, e.g. "vorher 6,7". */
  previousText: string;
  /** Full sentence for screen readers, e.g. "gestiegen von 6,7 auf 7,0 dKH". */
  srText: string;
}

export interface MetricCardProps {
  label: string;
  value: string;
  unit?: string;
  statusLabel: string;
  status: MetricStatus;
  /** Omit when there is no previous measurement (no arrow then). */
  trend?: MetricTrend;
  /** Where the value comes from when it is not a test, e.g. "Probe · GHL ProfiLux". */
  source?: string;
}

const TREND_ARROW: Record<MetricTrendDirection, string> = {
  up: '↑',
  down: '↓',
  stable: '→',
};

export function MetricCard({ label, value, unit, statusLabel, status, trend, source }: MetricCardProps) {
  return (
    <article className={`rg-metric-card rg-metric-card--${status}`}>
      <div className="rg-metric-card__glow" />
      <div className="rg-metric-card__accent" />
      <div className="rg-metric-card__label">{label}</div>
      <div className="rg-metric-card__value">
        {value}
        {unit && <span>{unit}</span>}
      </div>
      {trend && (
        <div className={`rg-metric-card__trend rg-metric-card__trend--${trend.direction}`} data-trend={trend.direction}>
          <span className="rg-metric-card__trend-arrow" aria-hidden="true">{TREND_ARROW[trend.direction]}</span>
          <span aria-hidden="true">{trend.previousText}</span>
          <span className="rg-metric-card__sr-only">{trend.srText}</span>
        </div>
      )}
      {source && <div className="rg-metric-card__source">{source}</div>}
      <div className="rg-metric-card__status">{statusLabel}</div>
    </article>
  );
}
